/**
 * @fileoverview Payment Failure/Cancel Page - Helpful Recovery
 * @module PaymentFailurePage
 * 
 * Design Philosophy:
 * - Consistent with LandingPage brand aesthetic and header design
 * - Helpful and supportive tone, not punitive
 * - Clear options for retry and support
 * - Maintains user confidence in the platform
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  MessageCircle,
  CreditCard,
  Shield,
  HelpCircle
} from 'lucide-react';

const PaymentFailurePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reason, setReason] = useState<string>('unknown');

  const Logo = () => (
    <div className="flex items-center gap-3">
      <img 
        src="/transparent-640-logo.png" 
        alt="Appeal Your Home" 
        className="w-10 h-10 object-contain"
      />
      <div className="flex flex-col">
        <h1 className="text-lg font-extrabold text-white tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          APPEAL YOUR HOME
        </h1>
        <p className="text-xs text-gray-300 font-medium tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Learn. Advocate. Save.
        </p>
      </div>
    </div>
  );

  // Determine failure reason from URL parameters
  useEffect(() => {
    // Load Montserrat font
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    const cancelled = searchParams.get('cancelled');
    const error = searchParams.get('error');
    
    if (cancelled === 'true') {
      setReason('cancelled');
    } else if (error) {
      setReason(error);
    } else {
      setReason('failed');
    }
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, [searchParams]);

  const getReasonDetails = () => {
    switch (reason) {
      case 'cancelled':
        return {
          title: 'Payment Cancelled',
          description: 'You cancelled the payment process. No charges were made to your account.',
          icon: <ArrowLeft className="w-12 h-12 text-orange-600" />,
          color: 'orange'
        };
      case 'card_declined':
        return {
          title: 'Card Declined',
          description: 'Your payment method was declined. Please try a different card or contact your bank.',
          icon: <CreditCard className="w-12 h-12 text-red-600" />,
          color: 'red'
        };
      case 'insufficient_funds':
        return {
          title: 'Insufficient Funds',
          description: 'Your payment method has insufficient funds. Please try a different payment method.',
          icon: <CreditCard className="w-12 h-12 text-red-600" />,
          color: 'red'
        };
      default:
        return {
          title: 'Payment Failed',
          description: 'We encountered an issue processing your payment. Please try again or contact support.',
          icon: <AlertCircle className="w-12 h-12 text-red-600" />,
          color: 'red'
        };
    }
  };

  const handleRetryPayment = () => {
    navigate('/pricing');
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleContactSupport = () => {
    window.open('mailto:support@appealyourhome.com?subject=Payment Issue&body=I encountered an issue with my payment. Please help.');
  };

  const reasonDetails = getReasonDetails();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header - Matches LandingPage Navigation */}
      <nav className="sticky top-0 w-full bg-gray-900/95 backdrop-blur-sm shadow-lg z-50 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button 
              onClick={handleBackToDashboard}
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Dashboard</span>
            </button>
            <Logo />
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Failure Hero */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-8">
            <div className={`w-24 h-24 bg-${reasonDetails.color}-50 border-2 border-${reasonDetails.color}-200 rounded-full flex items-center justify-center shadow-lg`}>
              {reasonDetails.icon}
            </div>
          </div>

          <h1 className="text-4xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{reasonDetails.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {reasonDetails.description}
          </p>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Retry Payment */}
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8">
            <div className="w-16 h-16 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center mb-6">
              <RefreshCw className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Try Again</h2>
            <p className="text-gray-600 mb-6 leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Ready to complete your purchase? Return to our pricing page to try a different payment method or retry your current one.
            </p>
            <button
              onClick={handleRetryPayment}
              className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl" style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <span>Back to Pricing</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Get Help */}
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8">
            <div className="w-16 h-16 bg-purple-50 border border-purple-100 rounded-lg flex items-center justify-center mb-6">
              <MessageCircle className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Need Help?</h2>
            <p className="text-gray-600 mb-6 leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our support team is here to help you resolve any payment issues. We'll respond within 24 hours.
            </p>
            <button
              onClick={handleContactSupport}
              className="w-full bg-gray-100 text-gray-900 px-6 py-4 rounded-lg font-bold text-lg hover:bg-gray-200 transition-all duration-300 border border-gray-300 flex items-center justify-center space-x-2" style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <MessageCircle className="w-5 h-5" />
              <span>Contact Support</span>
            </button>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <HelpCircle className="w-8 h-8 text-green-600" />
              <h2 className="text-3xl font-extrabold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>Common Issues & Solutions</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Payment Method Issues</h3>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <strong>Card Declined:</strong> Try a different card or contact your bank to ensure international transactions are enabled.
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <strong>Insufficient Funds:</strong> Check your account balance or try a different payment method.
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <strong>Expired Card:</strong> Update your card information with current expiration date.
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Technical Issues</h3>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <strong>Browser Issues:</strong> Try clearing your cache or using a different browser.
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <strong>Connection Problems:</strong> Check your internet connection and try again.
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <strong>Ad Blockers:</strong> Disable ad blockers that might interfere with payment processing.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Assurance */}
        <div className="text-center mt-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Shield className="w-6 h-6 text-green-600" />
            <span className="text-green-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Secure Payment Processing</span>
          </div>
          <p className="text-gray-500 text-sm max-w-2xl mx-auto font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Your payment information is processed securely through Stripe. We never store your credit card details on our servers. 
            All transactions are encrypted and PCI DSS compliant.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailurePage; 